<script setup lang="ts">
import { useVModel } from '@vueuse/core'
import { useFiles } from '@/composables'
import Button from '@/foundation/button/Button.vue'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/foundation/dialog'

type FileData = {
  fileName: string
  streamUrl: string
  mimetype: string
  id: string
}

const props = defineProps<{
  modelValue: boolean
  file: FileData
  url: string
}>()

const emit = defineEmits(['update:modelValue'])

const open = useVModel(props, 'modelValue', emit)
const { downloadFileHandler } = useFiles()
</script>

<template>
  <Dialog v-model:open="open">
    <DialogTrigger as-child> </DialogTrigger>
    <DialogContent>
      <DialogHeader>
        <DialogTitle
          ><div class="flex justify-between items-center">
            Preview: {{ file.fileName }}
            <Button class="btn-secondary ml-2" @click="downloadFileHandler(file)"> Download</Button>
          </div>
        </DialogTitle>
      </DialogHeader>

      <div class="my-2 max-h-[60vh] overflow-y-auto">
        <template v-if="props.file.mimetype.startsWith('image/')">
          <img :src="url" alt="Image preview" class="max-w-full rounded-md" />
        </template>
        <template v-else-if="props.file.mimetype === 'application/pdf'">
          <iframe :src="url" width="100%" height="500px" class="rounded-md"></iframe>
        </template>
        <template v-else-if="props.file.mimetype.startsWith('video/')">
          <video controls class="max-w-full rounded-md">
            <source :src="url" :type="props.file.mimetype" />
            Your browser does not support video playback.
          </video>
        </template>
        <template v-else-if="props.file.mimetype.startsWith('audio/')">
          <audio controls class="max-w-full rounded-md">
            <source :src="url" :type="props.file.mimetype" />
            Your browser does not support audio playback.
          </audio>
        </template>
        <template v-else>
          <p>Preview not available for this file type.</p>
          <a :title="props.file.fileName" class="cursor-pointer underline text-blue-600" @click="downloadFileHandler(file)">
            Download the file
          </a>
        </template>
      </div>

      <DialogFooter>
        <button class="btn-secondary" @click="open = false">Close</button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
